export const PUBLIC_LOCALES = [
  'cs',
  'sk',
  'en',
  'de',
  'es',
  'it',
  'pl',
] as const;
export type PublicLocale = (typeof PUBLIC_LOCALES)[number];

export function parsePublishedPublicLocales(value?: string): readonly PublicLocale[] {
  if (value === undefined) return PUBLIC_LOCALES;
  const requested = value.split(',').map((locale) => locale.trim());
  if (!requested.length || requested.some((locale) => !isPublicLocale(locale)) ||
      new Set(requested).size !== requested.length) {
    throw new Error('VITE_PUBLIC_LOCALES must be a non-empty, comma-separated list of unique supported locale codes.');
  }
  return Object.freeze(PUBLIC_LOCALES.filter((locale) => requested.includes(locale)));
}

// Publication is a build decision, not inferred from browser language or DNS.
// An unset variable keeps all translations available for local development.
export const PUBLISHED_PUBLIC_LOCALES = parsePublishedPublicLocales(
  import.meta.env.VITE_PUBLIC_LOCALES,
);

export const PUBLIC_ORIGINS: Record<PublicLocale, string> = {
  cs: 'https://parknuto.cz',
  sk: 'https://parknuto.sk',
  en: 'https://parknuto.com',
  de: 'https://parknuto.de',
  es: 'https://parknuto.es',
  it: 'https://parknuto.it',
  pl: 'https://parknuto.pl',
};

export const PUBLIC_OG_LOCALES: Record<PublicLocale, string> = {
  cs: 'cs_CZ',
  sk: 'sk_SK',
  en: 'en_GB',
  de: 'de_DE',
  es: 'es_ES',
  it: 'it_IT',
  pl: 'pl_PL',
};

export const PUBLIC_PATHS = {
  cs: {
    app: '/',
    partners: '/pro-mesta',
    coverage: '/pokryti',
    privacy: '/soukromi',
    terms: '/podminky',
  },
  sk: {
    app: '/',
    partners: '/pre-mesta',
    coverage: '/pokrytie',
    privacy: '/sukromie',
    terms: '/podmienky',
  },
  en: {
    app: '/',
    partners: '/for-cities',
    coverage: '/coverage',
    privacy: '/privacy',
    terms: '/terms',
  },
  de: {
    app: '/',
    partners: '/fuer-staedte',
    coverage: '/abdeckung',
    privacy: '/datenschutz',
    terms: '/nutzungsbedingungen',
  },
  es: {
    app: '/',
    partners: '/para-ciudades',
    coverage: '/cobertura',
    privacy: '/privacidad',
    terms: '/condiciones',
  },
  it: {
    app: '/',
    partners: '/per-le-citta',
    coverage: '/copertura',
    privacy: '/privacy',
    terms: '/condizioni',
  },
  pl: {
    app: '/',
    partners: '/dla-miast',
    coverage: '/zasieg',
    privacy: '/prywatnosc',
    terms: '/regulamin',
  },
} as const;

export function isPublicLocale(value: string | null): value is PublicLocale {
  return PUBLIC_LOCALES.some((locale) => locale === value);
}

function normalizeHostname(hostname: string) {
  return hostname.toLowerCase().replace(/\.$/, '');
}

export function isLocalPublicHostname(hostname: string): boolean {
  return ['localhost', '127.0.0.1', '[::1]', '::1'].includes(
    normalizeHostname(hostname),
  );
}

export function publicLocaleFromHostname(
  hostname: string,
): PublicLocale | undefined {
  const host = normalizeHostname(hostname);
  return PUBLIC_LOCALES.find((locale) => {
    const apex = new URL(PUBLIC_ORIGINS[locale]).hostname;
    return host === apex || host === `www.${apex}`;
  });
}

export function detectPublicLocale({
  hostname,
  pathname,
  search,
}: {
  hostname: string;
  pathname: string;
  search: string;
}): PublicLocale {
  if (pathname === '/cityops' || pathname.startsWith('/cityops/')) return 'cs';

  // Only these exact public hosts establish a production language. Neither a
  // foreign path, a query parameter nor a lookalike subdomain may override it.
  const hostLocale = publicLocaleFromHostname(hostname);
  if (hostLocale) return hostLocale;

  if (isLocalPublicHostname(hostname)) {
    const forcedLocale = new URLSearchParams(search).get('lang');
    if (isPublicLocale(forcedLocale)) return forcedLocale;
    const path = pathname.replace(/\/$/, '') || '/';
    return (
      PUBLIC_LOCALES.find((locale) =>
        Object.values(PUBLIC_PATHS[locale]).some((value) => value === path),
      ) ?? 'cs'
    );
  }
  return 'cs';
}

export function publicPath(
  route: 'app' | 'partners' | 'coverage' | 'privacy' | 'terms',
  locale: PublicLocale,
): string {
  return PUBLIC_PATHS[locale][route];
}

export function publicManifestPath(locale: PublicLocale): string {
  return locale === 'cs'
    ? '/manifest.webmanifest'
    : `/manifest-${locale}.webmanifest`;
}

export function localeSwitchUrl(
  targetLocale: PublicLocale,
  route: 'app' | 'partners' | 'coverage' | 'privacy' | 'terms',
  hostname: string,
): string {
  const path = publicPath(route, targetLocale);
  return isLocalPublicHostname(hostname)
    ? `${path}?lang=${targetLocale}`
    : `${PUBLIC_ORIGINS[targetLocale]}${path}`;
}
